// Local timings, wiring (U0). The shell reports a visit when MeeMail opens (or the owner comes
// back after a long break) and a heartbeat in each minute with input while the tab is visible;
// the feed and focus mode time card switches. Everything goes to MeeMail's own server in small
// batches and is never sent anywhere else.
import { useEffect } from "react";
import { timingApi } from "@web/lib/measureApi";
import {
  FLUSH_AFTER_MS,
  heartbeatDue,
  isReturn,
  minuteOf,
  SampleBuffer,
  SwitchTimer,
  visitId,
} from "./timings";

const buffer = new SampleBuffer();
const switches = new SwitchTimer();
let flushTimer: ReturnType<typeof setTimeout> | undefined;

function flushSamples(): void {
  clearTimeout(flushTimer);
  flushTimer = undefined;
  if (buffer.size) timingApi.samples(buffer.drain());
}

function addSample(ms: number): void {
  if (buffer.push({ kind: "card_switch", ms })) flushSamples();
  else flushTimer ??= setTimeout(flushSamples, FLUSH_AFTER_MS);
}

/** Call from the j/k handlers: the clock starts at the key press. */
export function markCardSwitch(fromKey: string | null): void {
  switches.start(performance.now(), fromKey);
}

/**
 * Call where the selected card renders: once `key` changes, the frame after the next paint ends
 * the pending switch (requestAnimationFrame runs before paint; the timeout lands after it).
 */
export function useCardPainted(key: string | null): void {
  useEffect(() => {
    if (!key) return;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const frame = requestAnimationFrame(() => {
      timeout = setTimeout(() => {
        const ms = switches.finish(performance.now(), key);
        if (ms !== null) addSample(ms);
      }, 0);
    });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
    };
  }, [key]);
}

const INPUT_EVENTS = ["keydown", "pointerdown", "wheel", "touchstart"] as const;

/** Visits and active minutes for the signed-in shell. */
export function useMeasureSession(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    let lastSeen = Date.now();
    let lastBeat: number | null = null;
    timingApi.session({ id: visitId(), elapsedMs: Math.round(performance.now()), reason: "load" });
    const seen = (now: number) => {
      if (isReturn(lastSeen, now))
        timingApi.session({ id: visitId(), elapsedMs: 0, reason: "return" });
      lastSeen = now;
    };
    const onInput = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      seen(now);
      if (!heartbeatDue(lastBeat, now)) return;
      lastBeat = minuteOf(now);
      timingApi.heartbeat();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") seen(Date.now());
      else flushSamples();
    };
    const opts = { capture: true, passive: true } as const;
    for (const name of INPUT_EVENTS) window.addEventListener(name, onInput, opts);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flushSamples);
    return () => {
      for (const name of INPUT_EVENTS) window.removeEventListener(name, onInput, opts);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flushSamples);
      flushSamples();
    };
  }, [enabled]);
}
